/**
 * /primeiro-acesso — container. O visual é o painel do design novo
 * (entrada/PainelPrimeiroAcesso), dentro da mesma moldura do login
 * (entrada/TelaDeEntrada, rota de layout em App.jsx): vindo do /login ou do
 * /mfa-verify, só o painel do meio troca.
 *
 * Aqui fica só o fluxo real: AuthContext.changePassword, a saída para o
 * sistema depois do "Senha definida" e o "Voltar ao login", que encerra a
 * sessão da senha provisória. O 2FA, quando ativo, já aconteceu antes desta
 * etapa (Login → MfaVerify → aqui), então depois dela vem o sistema.
 */
import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { DEFAULT_PATH } from "../../utils/rotas";
import PainelPrimeiroAcesso from "./entrada/PainelPrimeiroAcesso";

// Tempo para o "Senha definida" (verde) aparecer antes de sair da tela.
export const ESPERA_APOS_DEFINIR_MS = 800;

export default function PrimeiroAcesso() {
  const { authenticated, primeiroAcesso, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  // changePassword recarrega o /api/me, que zera `primeiroAcesso` ANTES de a
  // promessa resolver. Sem esta marca, o guard abaixo tiraria a pessoa da
  // tela antes de o botão mostrar "Senha definida".
  const [definindo, setDefinindo] = useState(false);
  const timerSaida = useRef(0);

  useEffect(() => () => clearTimeout(timerSaida.current), []);

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!primeiroAcesso && !definindo) {
    return <Navigate to={DEFAULT_PATH} replace />;
  }

  async function definirSenha({ provisoria, nova, confirmacao }) {
    setDefinindo(true);
    try {
      // silentError: o painel mostra o erro no campo certo; o toast repetiria.
      await changePassword(
        { senha_atual: provisoria, nova_senha: nova, confirmar_nova_senha: confirmacao },
        { silentError: true },
      );
    } catch (falha) {
      setDefinindo(false);
      throw falha;
    }
    timerSaida.current = setTimeout(() => navigate(DEFAULT_PATH, { replace: true }), ESPERA_APOS_DEFINIR_MS);
  }

  async function voltarAoLogin() {
    await logout();
    navigate("/login", { replace: true });
  }

  return <PainelPrimeiroAcesso definirSenha={definirSenha} onVoltar={voltarAoLogin} />;
}
